/**
 * Service per il controllo del cambio versione dell'applicazione
 * Ref: https://blog.nodeswat.com/automagic-reload-for-clients-after-deploy-with-angular-4-8440c9fdd96c
 */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogCloseResult } from '@progress/kendo-angular-dialog';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { appCommonConstants } from '../app.constants';
import { AppDialogService } from './dialog.service';
import { AppEventService } from './event.service';
import { AppLoggerService } from './logger.service';
import { AppNotificationService, NotifyTypeInfo } from './notification.service';

@Injectable({
  providedIn: 'root',
})

export class AppVersionCheckService {
  constructor(
    private http: HttpClient,
    private dialogService: AppDialogService,
    private eventService: AppEventService,
    private logger: AppLoggerService,
    private notificationService: AppNotificationService,
    private translate: TranslateService) { }

  private isShowingMessage = false;
  private url = '';
  /**
   * Checks in every set frequency the version of frontend application
   * @param url
   * @param {number} frequency - in milliseconds, defaults to 30 minutes
   */
  public init(url:string, frequency = 1000 * 60 * 30): void {
    this.url = url;
    setInterval(() => {
      if (!this.isShowingMessage) {
        this.versionCheck(url);
      }
    }, frequency);
  }

  /**
   * Verifica se sia variata la versione dell'app
   * @param url
   * @param showMessage visulizza l'eventuale messaggio di versione non variata
   */
  public versionCheck(url?: string, showMessage?: boolean): Subscription {
    // timestamp these requests to invalidate caches
    return this.http.get(`${url || this.url}?t=${new Date().getTime()}`)
      .subscribe(
        (response: any) => {
          const hashChanged = this.versionChanged(environment.appSettings.appVersion, response.version);
          if (!hashChanged) { // La versione non è variata
            if (showMessage) {
              this.logger.info({
                title: this.translate.instant('MESSAGE.VERSION_CHECK.TITLE'),
                message: this.translate.instant('MESSAGE.VERSION_UNCHANGED.MESSAGE'),
                nohistory: true
              });
            } else {
              this.eventService.broadcast(appCommonConstants.events.versionCheck, this.translate.instant('MESSAGE.VERSION_UNCHANGED.MESSAGE'));
            }

            return;
          }

          // If new version, do something
          this.isShowingMessage = true;
          let message = this.translate.instant('MESSAGE.NEW_VERSION_DETECTED.MESSAGE');
          let title = this.translate.instant('MESSAGE.NEW_VERSION_DETECTED.TITLE');

          // Aggiunge la notifica all'history
          this.notificationService.push({
            message: message,
            title: title,
            typeInfo: NotifyTypeInfo.WARNING
          });

          this.eventService.broadcast(appCommonConstants.events.versionCheck, message);
          // Visualizza dialog informativa, per reload pagina o relaoad manuale
          let dialog = this.dialogService.dialogOpen({
            actions: [
              {
                callback: () => {
                  dialog.close();
                },
                text: this.translate.instant('WORD.ANNULLA'),
              },
              {
                callback: () => {
                  location.reload();
                },
                text: this.translate.instant('WORD.RICARICA_PAGINA'),
                themeColor: "primary"
              }],
            content: `<div class="p-2">${message}</div>`,
            cssClassList: ['dialog-info'],
            isTitleHtml: true,
            maxWidth: window.innerWidth > 1024 ? 550 : 400,
            title: `<span><i class="fas fa-code-branch"></i>&nbsp;${title}</span>`
          });

          dialog.result.subscribe((result: any) => {
            this.isShowingMessage = false;
            if (!(result instanceof DialogCloseResult)) {
              if (typeof (result.callback) === "function") {
                result.callback();
              }
            }
          });
        }, (err) => {
          this.logger.error({ message: err, title: 'Could not get version' });
        }
      );
  }

  /**
   * Checks if hash has changed.
   * This file has the JS hash, if it is a different one than in the version.json
   * we are dealing with version change
   * @param currentVersion
   * @param newVersion
   * @returns {boolean}
   */
  private versionChanged(currentVersion:any, newVersion:any): boolean {
    if (currentVersion == null || newVersion == null) {
      return false;
    }

    return currentVersion !== newVersion;
  }
}
